import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styles from "./BSMDropdown.module.css";
import BSMSectionElement from "../BSMSectionElement";
import classNames from "classnames";
import Button from "../Button";
import SearchBar from "../SearchBar";
import { useSingleStockGet } from "./useSingleStockGet";
import { useStockGet } from "./useStockGet";
import Loader from "shared/Loader";
import BSMSearchItem from "../BSMSearchItem";

/**
 * View component. Displays BSM item content for a feed item section.
 */
export function BSMDropdown({ bsmItem, setBsmItem }) {
  const [stockId, setStockId] = useState("");
  const {
    data: allStockItems,
    refetch: refetchAll,
    isLoading: isLoadingAll,
    isError: isErrorAll,
  } = useStockGet();

  const {
    data: singleStockItem,
    refetch: refetchSingle,
    isLoading: isLoadingSingle,
  } = useSingleStockGet({ id: stockId });

  const [hidden, setHidden] = useState(false);
  const [light, setLight] = useState(false);
  const [selectedBsmItem, setSelectedBsmItem] = useState([]);

  useEffect(() => {
    if (bsmItem && bsmItem.length > 0) {
      bsmItem.map((item) => {
        setStockId(item.stockId);
      });
    }
  }, [bsmItem]);

  useEffect(() => {
    if (singleStockItem) {
      const newItems = [...selectedBsmItem, singleStockItem];
      setSelectedBsmItem(newItems);
    }
  }, [singleStockItem]);

  const [bsmItemsToShow, setBsmItemsToShow] = useState([]);

  // The BSM service can return the same stock item more than once;
  // dedupe by stockId so results (and React keys) are unique.
  const uniqueStockItems = useMemo(() => {
    const seen = new Set();
    return (allStockItems || []).filter((item) => {
      const id = item?.stockId?.toString();
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [allStockItems]);

  const bsmItemSearch = (value) => {
    const query = value.trim();

    // Require at least 2 characters so a single keystroke doesn't
    // dump hundreds of matches into the panel; still allow an exact
    // stockId match for single-character input (e.g. stockId "4").
    if (query.length < 2) {
      setBsmItemsToShow(
        query.length === 1
          ? uniqueStockItems.filter((item) => item.stockId.toString() === query)
          : []
      );
      return;
    }

    // Match on stockId or title
    const lowered = query.toLowerCase();
    const searchResults = uniqueStockItems.filter((item) => {
      return (
        item.stockId.toString().includes(query) ||
        (item.title || "").toLowerCase().includes(lowered)
      );
    });

    // Update state with the search results
    setBsmItemsToShow(searchResults);
  };

  const addBsmItem = (item) => {
    const newItems = [...selectedBsmItem, item];
    // setSelectedBsmItem(newItems);
    setBsmItem(newItems);
  };
  if (isLoadingAll || isLoadingSingle || !selectedBsmItem) {
    return <Loader />;
  }

  if (isErrorAll) {
    return (
      <div className={styles.container}>
        BSM items unavailable in this environment.
      </div>
    );
  }

  return (
    <div className={classNames(styles.none, styles.container)}>
      {!hidden ? (
        <div className={classNames(styles.none)}>
          <div onClick={() => setHidden(true)}>
            <BSMSectionElement bsmItem={bsmItem} />
          </div>
        </div>
      ) : (
        <div className={classNames(styles.border)}>
          <div className={styles.header}>
            <div>
              <Button
                text={"Done"}
                className={styles.btn}
                onClick={() => setHidden(false)}
              />
            </div>
          </div>
          <div className={styles.flex}>
            <div className={styles.bsmItemHeader}>BSM Item(s)</div>{" "}
            <div>
              Icon Color: &nbsp;
              <Button
                text={"Light"}
                className={classNames(styles.btn, styles.styledBtn, {
                  [styles.selected]: light,
                })}
                onClick={() => setLight(true)}
              />{" "}
              <Button
                text={"Dark"}
                className={classNames(styles.btn, styles.styledBtn, {
                  [styles.selected]: !light,
                })}
                onClick={() => setLight(false)}
              />
            </div>
          </div>
          <div className={styles.search}>
            {selectedBsmItem.map(
              (item) =>
                item.title && (
                  <div className={styles.title}>
                    {item.title} ({item.stockId})
                  </div>
                )
            )}
            <SearchBar
              placeholderText="Search BSM"
              onInput={(value) => bsmItemSearch(value)}
            />
            {bsmItemsToShow.length > 0 && (
              <div className={styles.searchResults}>
                {bsmItemsToShow.map((bsmItem) => (
                  <div key={bsmItem.stockId} className={styles.searchResult}>
                    <BSMSearchItem
                      bsmItem={bsmItem}
                      onAdd={(bsmItemToAdd) => addBsmItem(bsmItemToAdd)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

BSMDropdown.propTypes = {
  /**
   * BSM item content.
   */
  bsmItem: PropTypes.string,
  /**
   * Function that fires when a BSM item is selected.
   */
  setBsmItem: PropTypes.string,
};
